import Dropdown from '../../atoms/dropdown/dropdown';
import React, { useCallback, useMemo, useState } from 'react';
import styles from './course-details.module.scss';
import { ICourseData, ICourseInstance, IRequirements } from '../../../models/course/ICourseData';
import ITabs from '../../../models/tabs/ITabs';
import { replaceAttrSpaceWithHyphen } from '../../../utils/string-helpers/replace-attr-space-with-hyphen';
import Tabs from '../tabs/tabs';
import AccordionGroup from '../accordion-group/accordion-group';
import breakpoints from '../../../styles/partials/breakpoints.module.scss';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import CourseRequirements from '../course-requirements/course-requirements';
import CourseDates from '../course-dates/course-dates';
import {
  BrandField,
  BrandOptionFields,
  ICourseContent,
} from '../../../models/course/ICourseContent';
import Button from '../../atoms/button/button';
import { ButtonStyleType } from '../../../constants/button-style-type';
import { ICoursePageSettings } from '../../../models/course/ICoursePageSettings';
import CourseProgressionsList from '../course-progressions-list/course-progressions-list';
import CourseModules from '../course-modules/course-modules';
import CourseFeesAndPricing from '../course-fees-and-pricing/course-fees-and-pricing';
import { ServiceLevelType } from '../../../constants/service-level-type';
import {
  preferredProgramTooltipHeading,
  preferredProgramTooltipContent,
  preferredProgramAccessibilityLabel,
} from '../../../data/preferred-program';
import Markdown from 'markdown-to-jsx';
import IconTooltip from '../../molecules/icon-tooltip/icon-tooltip';
import CricosRequirements from '../../molecules/cricos-details/cricos-details';
import { SLAIcon } from '../../atoms/icons/icons';
import colorStyle from '../../../styles/utils/variables.module.scss';
import { SpecialCourse } from '../../../constants/special-course';
import Link from 'next/link';
import { CourseType } from '../../../constants/course-type';
import TagManager from 'react-gtm-module';
import { Country, CountryCode } from '../../../constants/country';
import CourseDatesUSPathway from '../course-dates/course-dates-us-pathway';
import { Term } from '../../../models/courses/term';
import { useTranslations } from 'next-intl';
import { Institutions } from '../../../constants/institutions';
import { ApplicationType } from '../../../constants/application-type';
import {
  FeesAndPricingData,
  getFeesAndPricingData,
} from '../course-fees-and-pricing/course-fees-and-pricing-calculation';
import dynamic from 'next/dynamic';
import ModalBox from '../modal-box/modal-box';
import IButton from '../../../models/button/IButton';

const CourseDetailsPdfButton = dynamic(
  () => import('../course-details-pdf-button/course-details-pdf-button'),
  {
    ssr: false,
  },
);

export type Props = {
  brandName: string;
  courseName: string;
  coursePageSettings: ICoursePageSettings;
  courseData: ICourseData;
  courseInstances: ICourseInstance[];
  courseContent: ICourseContent;
  courseSynonym: string;
  googleTagId?: string;
  iCasUndergraduateExists?: boolean;
  iCasGraduateExists?: boolean;
  currentLocale?: string;
  application?: string;
  buttonProps: IButton;
};

const extractCalendarYear = (academicYear: string): string => {
  const years = academicYear.split('/');
  return years[0];
};

export default function CourseDetails({
  brandName,
  courseName,
  coursePageSettings,
  courseData,
  courseInstances,
  courseContent,
  courseSynonym,
  googleTagId,
  iCasUndergraduateExists,
  iCasGraduateExists,
  currentLocale,
  application,
  buttonProps,
}: Props) {
  const [isBreakpointUp, setIsBreakpointUp] = useState(!deviceDetect.isMobile);

  useWindowResize(
    (newWidth) => setIsBreakpointUp(newWidth >= parseInt(breakpoints.tablet_landscape, 10)),
    100,
  );

  const translations = useTranslations();

  const staticCourseData = useMemo(
    () => getStaticCourseData(courseData, courseContent, courseInstances, courseName),
    [courseData, courseContent, courseInstances, courseName],
  );

  const [selectedAcademicYear, setSelectedAcademicYear] = useState<string>(
    () => staticCourseData.courseAcademicYears[0],
  );

  const [yearCourseData, setYearCourseData] = useState<YearCourseData>(() =>
    getYearCourseData(
      courseData,
      courseInstances,
      selectedAcademicYear,
      staticCourseData.isCountryCodeUS,
      courseContent?.brand?.fields,
    ),
  );

  const [selectedCourseDropdownValue, setSelectedCourseDropdownValue] = useState<string>(
    () => yearCourseData.courseInstancesDropdownDefaultValue,
  );

  const onChangeSelectedCourseDropdownValue = useCallback((value: string): void => {
    setSelectedCourseDropdownValue(value);
    setEntryRequirementsOverride(null);
  }, []);

  const onRequirementsChange = useCallback(
    (updatedEntryRequirements: IRequirements) => {
      const requirements = {
        ...updatedEntryRequirements,
      };

      fillEntryRequirementsFromCourse(courseData, requirements);

      setEntryRequirementsOverride(requirements);
    },
    [courseData],
  );

  const onChangeTab = useCallback(
    (tab: string): void => {
      const oldSelectedAcademicYear = selectedAcademicYear;

      if (tab === oldSelectedAcademicYear) {
        return;
      }

      const oldSelectedCourseDropdownValue = selectedCourseDropdownValue;
      const oldYearCourseData = yearCourseData;

      const oldProgramText = oldYearCourseData.courseInstancesDropdownOptions.find(
        (option) => option.value === oldSelectedCourseDropdownValue,
      )?.text;

      setSelectedAcademicYear(tab);

      const newYearCourseData = getYearCourseData(
        courseData,
        courseInstances,
        tab,
        staticCourseData.isCountryCodeUS,
        courseContent?.brand?.fields,
      );

      setYearCourseData(newYearCourseData);

      const newProgramItem = newYearCourseData.courseInstancesDropdownOptions.find(
        (option) => option.text === oldProgramText,
      );

      if (newProgramItem?.value) {
        newYearCourseData.courseInstancesDropdownDefaultValue = newProgramItem.value;
      }

      // Select a program with the same length as before for usability
      setSelectedCourseDropdownValue(newYearCourseData.courseInstancesDropdownDefaultValue);
      setEntryRequirementsOverride(null);
    },
    [
      courseContent,
      courseData,
      courseInstances,
      selectedAcademicYear,
      selectedCourseDropdownValue,
      staticCourseData.isCountryCodeUS,
      yearCourseData,
    ],
  );

  const [entryRequirementsOverride, setEntryRequirementsOverride] = useState<IRequirements>();

  const courseCalculatedData = useMemo(() => {
    const courseInstance = courseInstances.find(
      (courseInstance) =>
        courseInstance.academicYear === selectedAcademicYear &&
        (courseData.route === 'DirectEntry' ? courseInstance.programGroup : courseInstance.id) ===
          getCourseIdFromDropdownValue(selectedCourseDropdownValue),
    );

    const courseEntryRequirements = {
      ...courseInstance?.requirements,
      ...entryRequirementsOverride,
    };

    fillEntryRequirementsFromCourse(courseData, courseEntryRequirements);

    const brandFields = courseContent?.brand?.fields;

    const feesAndPricingData = getFeesAndPricingData(
      courseInstance?.tuitionFee,
      brandFields.feesAndPricing?.fields.fees,
      brandFields.feesAndPricing?.fields?.feesPricingNotes,
      brandFields.countryCode,
      courseData.route,
    );

    const tabs = getCourseDetailsTabs(
      courseInstance,
      courseData,
      courseContent,
      staticCourseData,
      feesAndPricingData,
      iCasGraduateExists,
      iCasUndergraduateExists,
      selectedAcademicYear,
      courseInstances,
      selectedCourseDropdownValue,
      coursePageSettings,
      currentLocale,
      courseEntryRequirements,
      onRequirementsChange,
      application,
    );

    const courseDetailsPdfData = getCourseDetailsPdfData(
      courseInstance,
      staticCourseData,
      feesAndPricingData,
      application,
      courseData,
      courseContent,
      courseInstances,
      selectedAcademicYear,
      selectedCourseDropdownValue,
      currentLocale,
      iCasUndergraduateExists,
      iCasGraduateExists,
      courseEntryRequirements,
      translations,
    );

    const academicYearsTabs = getAcademicYearsTabs(
      staticCourseData,
      courseContent,
      coursePageSettings,
    );

    return { courseInstance, feesAndPricingData, tabs, courseDetailsPdfData, academicYearsTabs };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    courseData,
    courseInstances,
    selectedAcademicYear,
    selectedCourseDropdownValue,
    entryRequirementsOverride,
  ]);
  // For unknown reason, cannot use CourseDetailsPdfButton below directly, because it always renders with old courseDetailsPdfData
  const PDFButton = useCallback(() => {
    if (
      !staticCourseData.isCountryCodeUS ||
      !staticCourseData.isPathway ||
      application === ApplicationType.UniBrandedB2c
    ) {
      return null;
    }

    return (
      <div className={styles[`pdf-button`]}>
        <CourseDetailsPdfButton
          courseDetailsPdfData={courseCalculatedData.courseDetailsPdfData}
          translations={translations}
          currentLocale={currentLocale}
          fileName={staticCourseData.pdfFileName}
        />
      </div>
    );
  }, [courseCalculatedData.courseDetailsPdfData, staticCourseData]);

  return (
    <div className={styles[`course-details`]}>
      <div className={styles[`course-title`]}>
        <h3 id='course-details'>{courseSynonym} Details</h3>
        <h4 className={styles[`course-name`]}>
          {courseName}
          {isBreakpointUp &&
            application == ApplicationType.PartnerPortal &&
            courseData?.serviceLevel?.includes(ServiceLevelType.Premium) && (
              <IconTooltip
                svgIcon={SLAIcon()}
                tooltipAccessibilityLabel={preferredProgramAccessibilityLabel}
                heading={preferredProgramTooltipHeading}
                svgIconColor={colorStyle.red100}
                activeSVGIconColor={colorStyle.red200}
                heapIdentifier={replaceAttrSpaceWithHyphen(preferredProgramTooltipHeading)}
                heapDataContent={courseName}
                helperClass='ms-2'
              >
                <Markdown>{preferredProgramTooltipContent()}</Markdown>
              </IconTooltip>
            )}
        </h4>
        <h5 className={styles[`university-name`]}>
          <Link href={coursePageSettings.brandURL}>{brandName}</Link>
        </h5>
      </div>
      <div className={`${styles[`academic-years`]} mt-4`}>
        <div className={styles.tabs}>
          <Tabs
            tabProps={courseCalculatedData.academicYearsTabs}
            onChange={onChangeTab}
            customClass='academic-years-tabs'
          />
          <div className={styles['program-dropdown']}>
            <Dropdown
              ariaLabel='Course Details Dropdown'
              onChange={onChangeSelectedCourseDropdownValue}
              options={yearCourseData?.courseInstancesDropdownOptions || []}
              id='selectedCourseDropdownValue'
              value={selectedCourseDropdownValue}
            />
          </div>
          <PDFButton />
        </div>
        {!!courseCalculatedData.tabs.length && (
          <>
            <AccordionGroup accordionProps={courseCalculatedData.tabs} />
            {/* show apply to the course button for mobile view */}
            {!isBreakpointUp && (
              <MobileApplyButton
                coursePageSettings={coursePageSettings}
                courseName={courseName}
                courseContent={courseContent}
                googleTagId={googleTagId}
                application={application}
                buttonProps={buttonProps}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function fillEntryRequirementsFromCourse(
  courseData: ICourseData,
  requirements: {
    minimumAge?: number;
    minimumAgeFootnote?: string;
    academic: string;
    language: string;
  },
) {
  if (!courseData || !requirements) {
    return;
  }

  if (!requirements.minimumAge) {
    requirements.minimumAge = courseData.minimumAge;
  }

  if (!requirements.minimumAgeFootnote) {
    requirements.minimumAgeFootnote = courseData.minimumAgeFootnote;
  }
}

// TODO: do not use --year in the dropdown values
function getCourseIdFromDropdownValue(value: string): React.SetStateAction<string> {
  return value?.split('--')[0];
}

function getAcademicYearsTabs(
  staticCourseData: StaticCourseData,
  courseContent: ICourseContent,
  coursePageSettings: ICoursePageSettings,
) {
  return staticCourseData.courseAcademicYears.map((year) =>
    getCourseAcademicYearTab(year, courseContent, coursePageSettings),
  );
}

const MobileApplyButton: React.FC<{
  coursePageSettings: ICoursePageSettings;
  courseName: string;
  courseContent;
  googleTagId: string;
  application: string;
  buttonProps: IButton;
}> = ({ coursePageSettings, courseName, courseContent, googleTagId, application, buttonProps }) => {
  // for redirect modal
  const [showModal, setShowModal] = useState(false);

  const modalFooterButton: IButton = {
    ...buttonProps,
    buttonURL: coursePageSettings.applyURL,
    buttonRole: 'link',
    buttonID: 'course-req-modalbox-apply-cta',
    buttonName: 'course-req-modalbox-apply-cta',
    buttonAriaLabel: `Apply to ${courseName}`,
    onClick: () => setShowModal(false),
    children: coursePageSettings?.redirectModal?.buttonText,
  };

  const pushDataLayer = useCallback(() => {
    if (!googleTagId) {
      return;
    }

    TagManager.dataLayer({
      dataLayer: {
        brandName: courseContent.brand.fields.brandName,
        courseName,
        event: 'applyButtonClick',
      },
    });

    // for redirect modal
    if (application == ApplicationType.UniBrandedB2c) {
      setShowModal(true);
    }
  }, [application, courseContent.brand.fields.brandName, courseName, googleTagId]);

  return (
    <>
      {application == ApplicationType.UniBrandedB2c && (
        <ModalBox
          isOpen={showModal}
          title={coursePageSettings?.redirectModal?.title}
          onClose={() => setShowModal(false)}
          footerButton={modalFooterButton}
          autoClickAfterDelay
          modalSize='lg'
          modalVerticallyCenter
        >
          {coursePageSettings?.redirectModal?.message}
        </ModalBox>
      )}
      <div className={`${styles[`course-details-apply`]} mt-4`}>
        {application == ApplicationType.UniBrandedB2c ? (
          <Button {...buttonProps} />
        ) : (
          <Button
            {...buttonProps}
            buttonNewTab='_blank'
            buttonURL={coursePageSettings.applyURL}
            buttonRole='link'
          />
        )}
      </div>
    </>
  );
};

function getCourseDetailsTabs(
  courseInstance: ICourseInstance,
  courseData: ICourseData,
  courseContent: ICourseContent,
  staticCourseData: StaticCourseData,
  feesAndPricingData: FeesAndPricingData,
  iCasGraduateExists: boolean,
  iCasUndergraduateExists: boolean,
  selectedAcademicYear: string,
  courseInstances: ICourseInstance[],
  selectedCourseDropdownValue: string,
  coursePageSettings: ICoursePageSettings,
  currentLocale: string,
  entryRequirements: IRequirements,
  onRequirementsChange: (entryRequirements: IRequirements) => void,
  application: string,
) {
  const tabs: ITabs[] = [];

  const brandOptions: BrandOptionFields = courseContent?.brand?.fields?.brandOptions?.fields;

  const brandFields = courseContent?.brand?.fields || ({} as BrandField);

  const displayiCASNote =
    brandFields.programRoutes?.find((p) => p == CourseType.ICAS) && staticCourseData.isPathway;

  const showDisclaimer =
    courseData.programGroup === SpecialCourse.IntegratedMasters ||
    courseData.programGroup === SpecialCourse.MastersInternationalDirect ||
    courseData.programGroup === SpecialCourse.InternationalDirect;

  // Entry Requirements
  tabs.push(
    getCourseRequirementsTab(
      coursePageSettings,
      courseData,
      entryRequirements,
      courseInstance,
      brandFields,
      displayiCASNote,
      iCasUndergraduateExists,
      iCasGraduateExists,
      showDisclaimer,
      onRequirementsChange,
      application,
      currentLocale,
    ),
  );

  if (!staticCourseData.isSpecialCourse) {
    tabs.push(
      getImportantDatesTab(
        courseInstances,
        courseInstance,
        staticCourseData,
        selectedAcademicYear,
        selectedCourseDropdownValue,
        currentLocale,
        coursePageSettings,
      ),
    );
  }

  // Fees and Pricing and Fees Notes
  if (brandFields.feesAndPricing?.fields?.fees?.length || courseInstance.tuitionFee) {
    const feesAndPricingTab = getFeesAndPricingTab(feesAndPricingData, coursePageSettings);

    tabs.push(feesAndPricingTab);
  }

  if (courseInstance.modules?.length) {
    tabs.push({
      id: 'modules',
      heading: brandOptions.modulesName ?? 'Modules',
      heapIdentifier: 'course-modules-tab',
      activeColor: coursePageSettings.backgroundColor,
      content: (
        <CourseModules
          brandCountry={brandFields.countryCode}
          modules={courseInstance.modules}
          brandOptions={brandOptions}
          degreeCredits={courseData.degreeCredits}
        />
      ),
    });
  }
  // Progressions
  if (courseInstance.progressions?.length > 0) {
    tabs.push({
      id: 'progressions',
      heading: staticCourseData.isSpecialCourse ? 'Degrees' : 'Progression',
      heapIdentifier: 'course-progressions-tab',
      activeColor: coursePageSettings.backgroundColor,
      content: (
        <CourseProgressionsList
          countryCode={brandFields.countryCode}
          progressions={courseInstance.progressions}
          pagingLimit={10}
          programRoute={courseData.route}
          progressionNote={brandOptions?.progressionNote}
        ></CourseProgressionsList>
      ),
    });
  }

  if (courseInstance.cricosCode) {
    tabs.push({
      id: 'cricos',
      heading: 'CRICOS Codes',
      heapIdentifier: 'cricos-tab',
      activeColor: coursePageSettings.backgroundColor,
      content: (
        <CricosRequirements
          cricosCode={courseInstance.cricosCode}
          programRoute={courseData.route}
        />
      ),
    });
  }

  return tabs;
}

function getCourseAcademicYearTab(
  year: string,
  courseContent: ICourseContent,
  coursePageSettings: ICoursePageSettings,
) {
  const brandOptions: BrandOptionFields = courseContent?.brand?.fields?.brandOptions?.fields;

  // course details tab data
  let academicYearsTabsData: ITabs = {
    id: year,
    heading:
      brandOptions?.universityCalendar === 'Calendar Year' ? extractCalendarYear(year) : year,
    heapIdentifier: 'course-academic-year-tab',
    activeColor: coursePageSettings.backgroundColor,
    content: null,
  };

  return academicYearsTabsData;
}

function getCourseDetailsPdfData(
  courseInstance: ICourseInstance,
  staticCourseData: StaticCourseData,
  feesAndPricingData: FeesAndPricingData,
  application: string,
  courseData: ICourseData,
  courseContent: ICourseContent,
  courseInstances: ICourseInstance[],
  selectedAcademicYear: string,
  selectedCourseDropdownValue: string,
  currentLocale: string,
  iCasUndergraduateExists: boolean,
  iCasGraduateExists: boolean,
  entryRequirements: IRequirements,
  translations: any,
) {
  const isSpecialCourse =
    courseData.route === 'DirectEntry' ||
    Object.values(SpecialCourse).includes(courseData.programGroup as SpecialCourse);

  const brandOptions: BrandOptionFields = courseContent?.brand?.fields?.brandOptions?.fields;

  const brandFields = courseContent?.brand?.fields || ({} as BrandField);

  const showDisclaimer =
    courseData.programGroup === SpecialCourse.IntegratedMasters ||
    courseData.programGroup === SpecialCourse.MastersInternationalDirect ||
    courseData.programGroup === SpecialCourse.InternationalDirect;

  const termsReportData = getTermsReportData(
    courseInstances,
    courseInstance,
    staticCourseData,
    selectedAcademicYear,
    selectedCourseDropdownValue,
    currentLocale,
  );

  const displayiCASNote =
    brandFields.programRoutes?.find((p) => p == CourseType.ICAS) && staticCourseData.isPathway;

  const isALevel: boolean = courseData?.level === 'ALevel';

  let iCasPageURL: string = null;
  const iCasCourseLevel: string =
    courseData?.level === 'Graduate' ? `postgraduate` : `undergraduate`;
  if (
    (courseData?.level === 'Graduate' && iCasGraduateExists) ||
    (courseData?.level === 'Undergraduate' && iCasUndergraduateExists)
  ) {
    iCasPageURL = `/universities/${brandFields.slug}/summary/icas-${iCasCourseLevel}`;
  }

  return {
    courseDuration: selectedAcademicYear + ' | ' + courseInstance?.programLength,
    academic: entryRequirements?.academic,
    language: entryRequirements?.language,
    minimumAge: entryRequirements?.minimumAge,
    minimumAgeFootnote: entryRequirements?.minimumAgeFootnote,
    disclaimerNote:
      showDisclaimer &&
      (courseData.programGroup === SpecialCourse.IntegratedMasters ||
        courseData.programGroup === SpecialCourse.MastersInternationalDirect ||
        courseData.programGroup === SpecialCourse.InternationalDirect) ? (
        <p>
          Entry requirements are subject to change due to the program selection and the academic
          year.
        </p>
      ) : null,
    classSize: courseInstance?.classSize || null,

    instanceID: courseInstance?.id,
    endNote: isALevel
      ? translations(`Courses.entryRequirements.noteALevel`)
      : translations(`Courses.entryRequirements.noteNonALevel`),

    iCasNote:
      displayiCASNote && brandFields.brandOptions?.fields?.iCasNote && iCasPageURL
        ? brandFields.brandOptions?.fields?.iCasNote
        : null,
    iCasURL:
      displayiCASNote && brandFields.brandOptions?.fields?.iCasNote && iCasPageURL
        ? iCasPageURL
        : null,

    feesAndPricingData,
    application: application,
    isUSPathway: staticCourseData.isCountryCodeUS && staticCourseData.isPathway,
    brandCountry: brandFields.countryCode,
    brandName: brandFields.brandName,
    brandLogo: brandFields.colorLogo?.fields?.image[0]?.secure_url,
    brandColor: brandFields.primaryColor,
    programTitle: courseData?.name,
    programLevel: courseData?.level,
    programRoute: courseData?.route,
    isSpecialCourse: isSpecialCourse,
    terms: termsReportData.terms,
    canShowSeasons: termsReportData.canShowSeasons,
    programLength: termsReportData.programLength,
    modules: courseInstance?.modules,
    brandOptions: brandOptions,
    progression: courseInstance?.progressions[0],
    degreeCredits: courseData.degreeCredits,
  };
}

type StaticCourseData = {
  courseAcademicYears: string[];
  isPathway: boolean;
  isCountryCodeUS: boolean;
  pdfFileName: string;
  isSpecialCourse: boolean;
  isCountryCodeUK: boolean;
  countryCode: string;
  brandTermName: string;
};

function getStaticCourseData(
  courseData: ICourseData,
  courseContent: ICourseContent,
  courseInstances: ICourseInstance[],
  courseName: string,
): StaticCourseData {
  const courseAcademicYears = Array.from(
    new Set(courseInstances?.map((item) => item.academicYear)),
  ).sort();

  const brandFields = courseContent?.brand?.fields || ({} as BrandField);

  const isPathway = courseData.route.toLowerCase() == CourseType.Pathway.toLowerCase();

  const isCountryCodeUS = brandFields.countryCode == Country.US;
  const isCountryCodeUK = brandFields.countryCode == CountryCode.UK;

  const brandName = courseContent?.brand.fields.brandName;

  const date = new Date();
  const timeStamp = `${date.getMonth() + 1}-${date.getDate()}-${date.getFullYear()}`;
  const pdfFileName = `${brandName.replaceAll(' ', '_')}-${courseName.replaceAll(' ', '_')}-${timeStamp}.pdf`;

  const isSpecialCourse =
    courseData.route === 'DirectEntry' ||
    courseData.programGroup === SpecialCourse.InternationalDirect ||
    courseData.programGroup === SpecialCourse.DirectEntry;

  const brandTermName = brandFields?.brandOptions?.fields?.termName;

  return {
    courseAcademicYears,
    isPathway,
    isCountryCodeUS,
    pdfFileName,
    isSpecialCourse,
    isCountryCodeUK,
    countryCode: brandFields.countryCode,
    brandTermName,
  };
}

type YearCourseData = ReturnType<typeof getYearCourseData>;

function getYearCourseData(
  courseData: ICourseData,
  courseInstances: ICourseInstance[],
  year: string,
  isCountryCodeUS: boolean,
  brandFields: BrandField,
) {
  const courseInstancesOfYear = courseInstances.filter(
    (courseInstance) => courseInstance.academicYear === year,
  );

  let isBridgeCourseAvailable =
    isCountryCodeUS && courseInstancesOfYear.some((c) => c.programType == 'Bridge');

  const courseInstancesValues = courseInstancesOfYear.map((courseInstance) => {
    let text = courseInstance.programLength;

    if (isBridgeCourseAvailable) {
      const title =
        courseInstance.programType == 'Bridge'
          ? courseInstance.programType
          : courseInstance.programGroup;

      text = `${courseInstance.programLength} : ${title}`;
    }

    return {
      text,
      id: replaceAttrSpaceWithHyphen(courseInstance.programLength),
      default: false,
      value: `${courseData.route === 'DirectEntry' ? courseInstance.programGroup : courseInstance.id}--${year}`,
    };
  });

  const courseInstancesDropdownOptions = [
    ...new Map(courseInstancesValues.filter(Boolean).map((item) => [item.text, item])).values(),
  ].sort((a, b) => a.text.localeCompare(b.text, 'en', { numeric: true }));

  const threeTermsIndex = courseInstancesDropdownOptions?.findIndex((o) => o.text == '3 Terms');

  const courseInstancesDropdownDefaultItemIndex =
    brandFields.countryCode == 'GB' && threeTermsIndex !== -1 ? threeTermsIndex : 0;

  const courseInstancesDropdownDefaultValue =
    courseInstancesDropdownOptions[courseInstancesDropdownDefaultItemIndex]?.value;

  return {
    courseInstancesDropdownOptions,
    courseInstancesDropdownDefaultValue,
  };
}

function getTermsReportData(
  courseInstances: ICourseInstance[],
  courseInstance: ICourseInstance,
  staticCourseData: StaticCourseData,
  selectedAcademicYear: string,
  selectedCourseDropdownValue: string,
  currentLocale: string,
) {
  // The Map is used to remove duplicates from different program instances
  let terms = new Map<string, Term>();
  let programLength: string;

  // const isUSPathway = staticCourseData.isCountryCodeUS && staticCourseData.isPathway;

  programLength = courseInstance?.programLength;

  // TODO: Logic c.programLength === programLength looks incorrect.
  const programInstances = courseInstances.filter(
    (c) => c.academicYear === selectedAcademicYear && c.programLength === programLength,
  );

  let hasTermDates = false;
  for (const programInstance of programInstances) {
    if (programInstance.termDates?.length > 0) {
      hasTermDates = true;
      break;
    }
  }

  const canShowSeasons =
    (staticCourseData.isCountryCodeUS || staticCourseData.isCountryCodeUK) && hasTermDates;

  if (canShowSeasons) {
    programInstances.forEach((programInstance) => {
      const firstProgression = programInstance.progressions[0];
      const isOSU = firstProgression?.institutionId === Institutions.OSU; // Oregon State University
      let temrDates = programInstance.termDates;
      // If the course has an integrated degree, the last term date should not be displayed in the "Important Dates" section.
      if (
        programInstance.programType === 'Extended Integrated' ||
        programInstance.programType === 'Integrated'
      ) {
        temrDates = programInstance.termDates.slice(0, -1);
      }
      temrDates.forEach((termDate) => {
        const term = new Term(
          {
            isOSU,
            countryCode: staticCourseData.countryCode,
            instanceId: programInstance.id,
            startDate: new Date(termDate.startDate),
            endDate: new Date(termDate.endDate),
          },
          currentLocale,
        );
        terms.set(term.id, term);
      });
    });
  } else {
    programInstances.forEach((programInstance) => {
      const term = new Term(
        {
          startDate: new Date(programInstance?.startDate),
          endDate: new Date(programInstance?.endDate),
        },
        currentLocale,
      );

      terms.set(term.id, term);
    });
  }

  const termsList = Array.from(terms.values()).sort(
    (a, b) => a.startDate.getTime() - b.startDate.getTime(),
  );

  // let prevYear: number = 0;
  // termsList.forEach((term) => {
  //   const startYear = term.startDate.getFullYear();

  //   term.isNextYear = startYear !== prevYear;

  //   prevYear = startYear;
  // });

  return {
    canShowSeasons,
    terms: termsList,
    programInstances,
    programLength,
    academicYear: courseInstance.academicYear,
  };
}

function getImportantDatesTab(
  courseInstances: ICourseInstance[],
  courseInstance: ICourseInstance,
  staticCourseData: StaticCourseData,
  selectedAcademicYear: string,
  selectedCourseDropdownValue: string,
  currentLocale: string,
  coursePageSettings: ICoursePageSettings,
) {
  const termsReportData = getTermsReportData(
    courseInstances,
    courseInstance,
    staticCourseData,
    selectedAcademicYear,
    selectedCourseDropdownValue,
    currentLocale,
  );

  const { terms, canShowSeasons, academicYear } = termsReportData;

  // Important Dates
  let content: React.ReactNode;

  if (canShowSeasons) {
    content = (
      <CourseDatesUSPathway
        terms={terms}
        brandTermName={staticCourseData.brandTermName}
        academicYear={academicYear}
        countryCode={staticCourseData.countryCode}
      />
    );
  } else {
    content = (
      <CourseDates
        terms={terms}
        programLength={termsReportData.programInstances[0]?.programLength}
      />
    );
  }

  const tab: ITabs = {
    id: 'important-dates',
    heading: 'Important Dates',
    heapIdentifier: 'course-important-dates-tab',
    activeColor: coursePageSettings.backgroundColor,
    content,
  };

  return tab;
}

function getCourseRequirementsTab(
  coursePageSettings: ICoursePageSettings,
  courseData: ICourseData,
  entryRequirements: IRequirements,
  courseInstance: ICourseInstance,
  brandFields: BrandField,
  displayiCASNote: boolean,
  iCasUndergraduateExists: boolean,
  iCasGraduateExists: boolean,
  showDisclaimer: boolean,
  onRequirementsChange: (entryRequirements: IRequirements) => void,
  application: string,
  currentLocale: string,
): ITabs {
  return {
    id: 'entry-requirements',
    heading: 'Entry requirements',
    heapIdentifier: 'course-entry-requirements-tab',
    activeColor: coursePageSettings.backgroundColor,
    content: (
      <CourseRequirements
        showDisclaimer={showDisclaimer}
        entryRequirements={entryRequirements}
        classSize={courseInstance.classSize}
        brandSlug={brandFields.slug}
        courseLevel={courseData?.level}
        displayiCASNote={displayiCASNote}
        iCasNote={brandFields.brandOptions?.fields?.iCasNote}
        iCasUndergraduateExists={iCasUndergraduateExists}
        iCasGraduateExists={iCasGraduateExists}
        regionalQualifications={courseInstance.regionalQualifications}
        countries={courseInstance.countries}
        instanceID={courseInstance.id}
        onRequirementsChange={onRequirementsChange}
        currentLocale={currentLocale}
        applicationType={application}
      />
    ),
  };
}

function getFeesAndPricingTab(
  feesAndPricingData: FeesAndPricingData,
  coursePageSettings: ICoursePageSettings,
): ITabs {
  return {
    id: 'fees-and-pricing',
    heading: 'Fees and Pricing',
    heapIdentifier: 'course-fees-and-pricing-tab',
    activeColor: coursePageSettings.backgroundColor,
    content: <CourseFeesAndPricing feesAndPricingData={feesAndPricingData} />,
  };
}
